import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { IBannerReponse } from '../../../model/base/response/banner-response';
import { useMenuLinkNavigation } from '../../../hooks/useMenuLinkNavigation';
import { RADIUS } from '../../../theme/tokens';

const AUTOPLAY_INTERVAL = 6000;

interface HeroBannerProps {
  banners: IBannerReponse[];
}

// Controles translúcidos sobre a imagem, com alvo de toque de 44px
const controlSx = {
  width: 44,
  height: 44,
  color: 'common.white',
  backgroundColor: 'rgba(15, 23, 42, 0.45)',
  backdropFilter: 'blur(4px)',
  '&:hover': {
    backgroundColor: 'rgba(15, 23, 42, 0.7)',
  },
} as const;

/**
 * Hero logo abaixo da navegação, na largura do container do conteúdo
 * (quem usa envolve num Container); título e controles ficam sobre a imagem.
 */
export default function HeroBanner({ banners }: HeroBannerProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [imageErrors, setImageErrors] = useState<{ [key: number]: boolean }>({});
  const [paused, setPaused] = useState(false);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const maxSteps = banners?.length || 0;
  const openLink = useMenuLinkNavigation();
  const autoplay = maxSteps > 1 && !paused && !prefersReducedMotion;

  useEffect(() => {
    if (!autoplay) return;

    const timer = setInterval(() => {
      setActiveStep((prevActiveStep) => (prevActiveStep + 1) % maxSteps);
    }, AUTOPLAY_INTERVAL);

    return () => clearInterval(timer);
  }, [autoplay, maxSteps, activeStep]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => (prevActiveStep + 1) % maxSteps);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => (prevActiveStep - 1 + maxSteps) % maxSteps);
  };

  const handleImageError = (index: number) => {
    setImageErrors((prev) => ({ ...prev, [index]: true }));
  };

  if (!banners || banners.length === 0) {
    return null;
  }

  const activeBanner = banners[activeStep];

  return (
    <Box
      role="region"
      aria-roledescription="carrossel"
      aria-label="Destaques"
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: { xs: '4 / 3', sm: '16 / 6', lg: '16 / 5' },
        minHeight: 240,
        maxHeight: 560,
        overflow: 'hidden',
        borderRadius: `${RADIUS.card}px`,
        backgroundColor: 'grey.800',
      }}
    >
      {/* Slides (crossfade) */}
      {banners.map((banner, index) => {
        const isActive = index === activeStep;
        return (
          <Box
            key={index}
            role="group"
            aria-roledescription="slide"
            aria-label={`${index + 1} de ${maxSteps}`}
            aria-hidden={!isActive}
            sx={{
              position: 'absolute',
              inset: 0,
              opacity: isActive ? 1 : 0,
              transition: 'opacity 300ms ease-out',
              // Só o banner visível recebe clique (os demais ficam empilhados, transparentes)
              pointerEvents: isActive ? 'auto' : 'none',
              cursor: banner.url ? 'pointer' : 'default',
            }}
            onClick={banner.url ? () => openLink(banner.url, banner.openInNewTab) : undefined}
          >
            {imageErrors[index] ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.brand.deep}, ${alpha(theme.palette.primary.main, 0.85)})`,
                }}
              />
            ) : (
              <Box
                component="img"
                src={banner.urlImage}
                alt={banner.title || ''}
                loading={index === 0 ? 'eager' : 'lazy'}
                onError={() => handleImageError(index)}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                }}
              />
            )}
          </Box>
        );
      })}

      {/* Degradê inferior para legibilidade do título e dos controles */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 'auto 0 0 0',
          height: '55%',
          background: 'linear-gradient(to top, rgba(15, 23, 42, 0.78), rgba(15, 23, 42, 0.35) 55%, transparent)',
          pointerEvents: 'none',
        }}
      />

      {/* Título e controles sobre a imagem */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          px: { xs: 2, md: 4 },
          pb: { xs: 2, md: 4 },
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2,
          pointerEvents: 'none',
        }}
      >
        <Box aria-live={autoplay ? 'off' : 'polite'} sx={{ minWidth: 0, maxWidth: 760 }}>
          {activeBanner?.title && (
            <Typography
              component="p"
              sx={{
                color: 'common.white',
                fontFamily: (theme) => theme.typography.h1.fontFamily,
                fontWeight: 600,
                fontSize: { xs: '1.125rem', sm: '1.5rem', md: '2.25rem' },
                lineHeight: 1.2,
                letterSpacing: '-0.015em',
                textShadow: '0 1px 12px rgba(15, 23, 42, 0.4)',
                textWrap: 'balance',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {activeBanner.title}
            </Typography>
          )}
        </Box>

        {maxSteps > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, pointerEvents: 'auto' }}>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.75, mr: 1 }}>
              {banners.map((banner, index) => (
                <Box
                  key={index}
                  component="button"
                  type="button"
                  onClick={() => setActiveStep(index)}
                  aria-label={`Ir para o destaque ${index + 1}`}
                  aria-current={index === activeStep ? 'true' : undefined}
                  sx={{
                    // Área de toque de 24px com marcador visual menor
                    width: index === activeStep ? 32 : 24,
                    height: 24,
                    p: 0,
                    border: 0,
                    cursor: 'pointer',
                    background: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'width 200ms',
                    '&::before': {
                      content: '""',
                      display: 'block',
                      width: '100%',
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: index === activeStep ? 'common.white' : 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                />
              ))}
            </Box>
            <IconButton onClick={handleBack} aria-label="destaque anterior" sx={controlSx}>
              <KeyboardArrowLeft />
            </IconButton>
            <IconButton
              onClick={() => setPaused(!paused)}
              aria-label={paused ? 'retomar rotação dos destaques' : 'pausar rotação dos destaques'}
              sx={controlSx}
            >
              {paused ? <PlayArrowIcon /> : <PauseIcon />}
            </IconButton>
            <IconButton onClick={handleNext} aria-label="próximo destaque" sx={controlSx}>
              <KeyboardArrowRight />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
}
